import {
  type RentalManagerUserPropertyMultiplexWebApiHandler_setListingCriteria_Args,
  type RentalManagerUserPropertyMultiplexWebApiHandler_setListingCriteria_Options,
  useRentalManagerUserPropertyMultiplexWebApiHandler_setListingCriteria,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import type { Client } from '@zg-rentals/http-client';
import type { UseMutationOptions } from '@zg-rentals/react-query5';
import type {
  ApiResponseErrorDto,
  MultiplexBuildingSessionListingCriterionRequestWebDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import type { CriterionProps } from '../../../model/ListingCriteria/Criterion';

/**
 * Same shape as `UserPropertyMultiplexSetListingCriteriaRequestWebDto` from the generated API client, sourced from the
 * multiplex handler `setListingCriteria` mutation args.
 */
export type UserPropertyMultiplexSetListingCriteriaRequestWebDto =
  RentalManagerUserPropertyMultiplexWebApiHandler_setListingCriteria_Args['postData'];

/**
 * Same shape as `UserPropertyMultiplexSetListingCriteriaResponseWebDto` from the generated API client, sourced from the
 * multiplex handler `setListingCriteria` mutation result type (generated hook lives in
 * `@zg-rentals/ts-rental-manager-api-client-react-query`).
 */
export type UserPropertyMultiplexSetListingCriteriaResponseWebDto =
  RentalManagerUserPropertyMultiplexWebApiHandler_setListingCriteria_Options extends UseMutationOptions<
    infer Data,
    unknown,
    RentalManagerUserPropertyMultiplexWebApiHandler_setListingCriteria_Args
  >
    ? Data
    : never;

/** Maps app criterion models to the multiplex `setListingCriteria` request rows. */
export const mapCriterionToMultiplexListingCriterionRequest = (
  c: CriterionProps,
): MultiplexBuildingSessionListingCriterionRequestWebDto => ({
  criterion: c.criterion,
  negotiable: Boolean(c.negotiable),
  refundable: Boolean(c.refundable),
  type: c.type as MultiplexBuildingSessionListingCriterionRequestWebDto['type'],
});

const toApiResponseErrorDto = (error: unknown): ApiResponseErrorDto<unknown> | undefined => {
  if (!DatarouterError.is(error)) {
    return undefined;
  }

  return {
    code: error.code ?? '',
    message: error.message,
    data: error.errorDetails,
  };
};

/**
 * Runtime guard beyond compile-time args:
 * `getParams.multiplexBuildingSessionId` must be a non-empty string; `postData` must be a plain object (not an array).
 */
const assertMultiplexSetListingCriteriaMutationArgs = (
  args: RentalManagerUserPropertyMultiplexWebApiHandler_setListingCriteria_Args,
): void => {
  const { postData, getParams } = args;

  if (typeof postData !== 'object' || postData === null || Array.isArray(postData)) {
    throw new TypeError(
      'useMultiplexSetListingCriteria: `postData` must be a non-array object (UserPropertyMultiplexSetListingCriteriaRequestWebDto).',
    );
  }

  if (typeof getParams !== 'object' || getParams === null) {
    throw new TypeError('useMultiplexSetListingCriteria: `getParams` is required (SetListingCriteriaParams).');
  }

  const sessionId = getParams.multiplexBuildingSessionId;

  if (typeof sessionId !== 'string' || sessionId.trim() === '') {
    throw new TypeError(
      'useMultiplexSetListingCriteria: `getParams.multiplexBuildingSessionId` is required on every call (non-empty string).',
    );
  }
};

export type UseMultiplexSetListingCriteriaOptions = Omit<
  RentalManagerUserPropertyMultiplexWebApiHandler_setListingCriteria_Options,
  'mutationFn'
> & {
  httpClient?: Client;
};

type BaseMultiplexSetListingCriteriaMutation = ReturnType<
  typeof useRentalManagerUserPropertyMultiplexWebApiHandler_setListingCriteria
>;

export type UseMultiplexSetListingCriteriaResult = Omit<
  BaseMultiplexSetListingCriteriaMutation,
  'mutate' | 'mutateAsync' | 'data' | 'error' | 'isPending'
> & {
  mutate: (
    args: RentalManagerUserPropertyMultiplexWebApiHandler_setListingCriteria_Args,
    options?: Parameters<BaseMultiplexSetListingCriteriaMutation['mutate']>[1],
  ) => ReturnType<BaseMultiplexSetListingCriteriaMutation['mutate']>;
  mutateAsync: (
    args: RentalManagerUserPropertyMultiplexWebApiHandler_setListingCriteria_Args,
    options?: Parameters<BaseMultiplexSetListingCriteriaMutation['mutateAsync']>[1],
  ) => ReturnType<BaseMultiplexSetListingCriteriaMutation['mutateAsync']>;
  data: BaseMultiplexSetListingCriteriaMutation['data'];
  error: BaseMultiplexSetListingCriteriaMutation['error'];
  isPending: BaseMultiplexSetListingCriteriaMutation['isPending'];
  errors: ApiResponseErrorDto<unknown> | undefined;
  success: boolean | undefined;
  errorMessage: UserPropertyMultiplexSetListingCriteriaResponseWebDto['errorMessage'] | undefined;
  units: UserPropertyMultiplexSetListingCriteriaResponseWebDto['units'] | undefined;
};

/**
 * Multiplex set listing criteria: wraps the generated `setListingCriteria` mutation (POST
 * `/api/web/v1/users/properties/multiplex/setListingCriteria`).
 *
 * Merges default `meta`: invalidates tag `multiplexBuildingSessionDetails` (plus any `meta.invalidates` you pass) and
 * sets `meta.context` for logging unless overridden.
 *
 * Exposes the usual mutation API (`mutate`, `mutateAsync`, `reset`, `data`, `error`, `isPending`, ...). Also returns
 * `errors` (normalized shape when the request throws), and `success`, `errorMessage`, and `units` from the last
 * successful `data`.
 *
 * **Calling the mutation:** Use `{ getParams: { multiplexBuildingSessionId }, postData }` where `postData` matches
 * {@link UserPropertyMultiplexSetListingCriteriaRequestWebDto}. Compile-time typing applies to `mutate` / `mutateAsync`;
 * runtime checks enforce non-array `postData`, `getParams`, and a non-empty session id string.
 */
const useMultiplexSetListingCriteria = (
  mutationOptions: UseMultiplexSetListingCriteriaOptions = {},
): UseMultiplexSetListingCriteriaResult => {
  const { meta: metaOverrides, ...restMutationOptions } = mutationOptions;

  const mutationResult = useRentalManagerUserPropertyMultiplexWebApiHandler_setListingCriteria({
    ...restMutationOptions,
    meta: {
      ...metaOverrides,
      invalidates: ['multiplexBuildingSessionDetails', ...(metaOverrides?.invalidates ?? [])],
      context: {
        api: 'useRentalManagerUserPropertyMultiplexWebApiHandler_setListingCriteria',
        details: 'useMultiplexSetListingCriteria',
        ...metaOverrides?.context,
      },
    },
  });

  const { data, error, isPending, mutate, mutateAsync, ...rest } = mutationResult;

  return {
    ...rest,
    mutate: (args, options) => {
      assertMultiplexSetListingCriteriaMutationArgs(args);
      return mutate(args, options);
    },
    mutateAsync: (args, options) => {
      assertMultiplexSetListingCriteriaMutationArgs(args);
      return mutateAsync(args, options);
    },
    data,
    error,
    isPending,
    errors: toApiResponseErrorDto(error),
    success: data?.success,
    errorMessage: data?.errorMessage,
    units: data?.units,
  };
};

export default useMultiplexSetListingCriteria;
